import { ElementRef, Injectable } from '@angular/core';
import { Observable, from, of, BehaviorSubject } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

import { createClient, Entry } from 'contentful';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import DOMPurify from 'dompurify';
import { parse } from 'angular-html-parser';

import { environment } from '@src/environments/environment';
import { ctfConfig } from '@src/config/contentful.config';
import { CustomEntry } from '../interfaces/customEntry';
import { debounce } from './general.service';
import { AnalyticsService } from './fb-analytics.service';

@Injectable({
  providedIn: 'root'
})

export class ContentService {
  private config = ctfConfig;

  private cdaClient = createClient({
    space: this.config.space,
    accessToken: this.config.accessToken
  });

  private cpaClient = createClient({
    space: this.config.space,
    accessToken: this.config.previewAccessToken,
    host: this.config.previewHost
  });

  private client = !environment.production ? this.cpaClient : this.cdaClient;

  private d2hOpt: object = {
    renderMark: {
      [MARKS.CODE]: text => text.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    },
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: node => `<img src="${(node.data.target.fields.file.url)}" width="100%" />`
    }
  };

  private entrySubject = new BehaviorSubject<Entry<any>>(null);
  private entriesSubject = new BehaviorSubject<Entry<any>[]>([]);
  private entryStateSubject = new BehaviorSubject<boolean>(false);
  private searchSubject = new BehaviorSubject<Entry<any>[]>(null);
  private filterSubject = new BehaviorSubject<Entry<any>[]>(null);

  entry = this.entrySubject.asObservable();
  entries = this.entriesSubject.asObservable();
  entryState = this.entryStateSubject.asObservable();

  placeholderImg = this.client.getAsset('4PWfrm8hPKHUm1RuTGXHBp').then(
    (res) => res
  )

  nullFields = {
    categories: ['Uncategorized'],
    tags: [{sys: {type: null, linkType: null, id: 'untagged'}}],
    default: 'Undefined'
  }

  constructor(
    private analyticsService: AnalyticsService
  ) { }

  private handleError<T>(result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      
      if (result)
        return of(result as T);

      return of(error as T);
    };
  }
  
  docToHTML(doc: any, opt?: any): any {
    if (!opt)
      return documentToHtmlString(doc, this.d2hOpt);
      
    return documentToHtmlString(doc, opt);
  }

  parseData(data: any): any {
    const parser = (val) => {
      return parse(DOMPurify.sanitize(val));
    },

    parseStr = (obj) => {
      if (typeof obj === 'object') {
        Object.keys(obj).map((key) => {
          if (typeof obj[key] === 'string') {
            obj[key] = parser(obj[key]);
          } else {
            parseStr(obj[key]);
          }
        })
      };

      return obj;
    };

    return parseStr(data);
  }

  tagIDtoName(id: string): string {
    if (id === '')
      return id;

    const sepaRegex = /([A-Z0-9])+/g,
          separator = '-';

    return id.split('').map((e) => e.match(sepaRegex) ? separator + e.toLowerCase() : e).join('');
  }

  slugToID(slug: string, entries: Entry<any>[] = this.entriesSubject.value): string {
     if (entries.map(e => e.sys.id).includes(slug))
      return slug;

     return entries.find(e => e.fields?.slug === slug)?.sys.id || undefined;
  }

  trackContentLinks(element: ElementRef, trackDesc?: string): void {
    const anchors = element.nativeElement.querySelectorAll('a');
    const processEvent = ($event) => {
      this.analyticsService.trackInput($event);
    }

    if (anchors.length) {
      anchors.forEach(async(e) => {
        await e.removeEventListener('click', processEvent);
        e.dataset.content = trackDesc || 'Blog Post Link';
        e.addEventListener('click', processEvent);
      })
    }
  }

  getDataByID(id: string, dataPoint: string, entries: Entry<any>[] = this.entriesSubject.value): any {
    const entry = entries.find(e => e.sys.id === id),
          dataTree = dataPoint.split('.');

    if (entry) {
      return dataTree.reduce((acc, curr) => {
        if (acc)
          return acc[curr] || undefined;
      }, entry)
    }

    return undefined;
  }

  getExcerpt(text: string, charLimit: number, clipToSentence: boolean, removeHTML: boolean = true): string {
    if (removeHTML)
      text = text.replace(/<(img)[^>]*>/g, '');

    if (text.length <= charLimit)
      return text;

    const raw = text.slice(0, charLimit - 1);
    const periodRegex = /\.(?=\s*[^.]*$)/g;
    const lastPunctRegex = /[-—_.,;:]+$/;
    const excerpt = raw.slice(
      0, 
      clipToSentence && raw.search(periodRegex) > -1 ? 
      raw.search(periodRegex) :
      raw.lastIndexOf(' ')
    ).replace(lastPunctRegex, '');

    return `${excerpt}...`;
  }

  async citeImages(content: HTMLElement): Promise<void> {
    return new Promise((rslv) => {
      const tags = content.getElementsByTagName('img'),
            alignClasses = ['left', 'center', 'right'],
            images = [];

      if (!tags.length)
        return rslv(null);

      Array.from(tags).forEach((t) => {
        const image: any = {},
              src = new URL(t.src),
              seg = src.pathname.split('/');

        image.tag = t;
        image.hostname = src.hostname;

        if (image.hostname.includes('ctfassets'))
          image.id = seg[2];
        else
          image.id = null;

        images.push(image);
      });

      images.map(async(e, i, a) => {
        if (e.hostname.includes('ctfassets'))
          await this.client.getAsset(e.id).then(
            (res) => e.description = res.fields.description
          )
        else 
          e.description = null;

        if (e.description) {
          const imgTag = content.querySelector(`img[src*='${e.id}']`);

          imgTag.insertAdjacentHTML(
            'afterend',
            `<cite class=${imgTag.classList.value.split(' ').find(e => alignClasses.includes(e))}>${e.description}</cite>`
          );
        }

        if (i === a.length - 1)
          setTimeout(() => {rslv(e)}, 200);
      });
    })
    
  }

  prioritizeEntries(d: any[], f: string) {
    const sorted = [],
      fType = typeof d.find(e => e.fields[f])?.fields[f];

    if (fType === 'boolean')
      d.forEach(e => {
        if (e.fields[f]) {
          sorted.push(e);
        }
      });

    d.forEach(e => {
      if (!sorted.includes(e)) {
        sorted.push(e);
      }
    });

    return sorted;
  }

  @debounce(1500)
  debTrackSearch(term: string): void {
    this.analyticsService.logEvent(
      'search',
      {
        search_term: term
      }
    );
  }

  getEntries(contentType?: string, order?: string, query?: object, prioritize?: string): Observable<Entry<any>[]> {
    return from(this.client.getEntries(
      Object.assign({
        content_type: contentType || this.config.contentTypeIDs.entries,
        order: order || '-sys.createdAt'
      }, query)
    ))
    .pipe(
      map(res => {
        res.items = res.items.map(e => {
          if (!e.metadata.tags.length)
            e.metadata.tags = this.nullFields.tags;

          if (!e.fields['categories'])
            e.fields['categories'] = this.nullFields.categories;

          return e;
        });

        if (!contentType || contentType === 'entries') {
          this.entriesSubject.next(res.items);
        }

        if (prioritize)
          return this.prioritizeEntries(res.items, prioritize);

        return res.items;
      }),
      catchError(this.handleError<Entry<any>[]>([]))
    )
  }

  getEntry(id: string | null, contentType?: string, gaEvent: boolean = true): Observable<Entry<any>> | Observable<null> {
    if (!id) {
      return of(null).pipe(
        map(res => {
          if (!contentType || contentType === 'entries') {
            this.entrySubject.next(null);
            this.entryStateSubject.next(false);
          }

          return res;
        })
      );
    }

    return from(this.client.getEntry(
      id,
      Object.assign({
        content_type: (
          contentType ? 
          this.config.contentTypeIDs[contentType] :
          this.config.contentTypeIDs.entries
        )
      })
    ))
    .pipe(
      map((res: CustomEntry) => {
        if (!res.metadata.tags.length)
          res.metadata.tags = this.nullFields.tags;

        if (!res.fields['categories'])
          res.fields['categories'] = this.nullFields.categories;

        if (!contentType || contentType === 'entries') {
          this.entrySubject.next(res);
          this.entryStateSubject.next(true);
        }

        if (gaEvent) {
          this.analyticsService.logEvent(
            'select_item',
            {
              item_list_id: res.sys.contentType.sys.id,
              item_list_name: res.fields?.entryType?.join(', ') || 'entries',
              items: [
                {
                  item_id: res.sys.id,
                  item_name: res.fields.title
                }
              ],
              ev_item_id: res.sys.id,
              ev_item_name: res.fields.title
            }
          );
        }

        return res;
      }),
      catchError(this.handleError<Entry<any>>(null))
    )
  }

  searchEntries(entries: Entry<any>[], str: string, gaEvent: boolean = true): Observable<Entry<any>[]> {
    const term = str.toLowerCase(),
          results = entries.filter((el) => {
            const criteria = (
              el.metadata.tags.map(
                e => this.tagIDtoName(e.sys.id)
              ).join(' ') + 
              el.fields.title +
              el.fields.description +
              (el.fields.categories || ['']).join(' ')
            ).toLowerCase();

            if (criteria.includes(term)) {
              return el;
            }
          });

    return of(results).pipe(
      tap((data) => {
        this.searchSubject.next(data);

        if (gaEvent) 
          this.debTrackSearch(str);
        
        this.mergeQuery(
          this.filterSubject, 
          this.searchSubject,
          data
        );
      })
    )
  }

  filterEntries(entries: Entry<any>[], str: string, gaEvent: boolean = true): void {
    const term = str.toLowerCase(),
          results = entries.filter((el) => {
            const criteria = (el.fields.categories || ['']).join(' ').toLowerCase();
            
            if (criteria.includes(term)) {
              return el;
            }
          });

    this.filterSubject.next(results);

    if (gaEvent) {
      this.analyticsService.logEvent(
        'select_content',
        {
          content_type: str
        }
      );
    }
    
    this.mergeQuery(
      this.searchSubject, 
      this.filterSubject,
      results
    );
  }

  mergeQuery(subj: BehaviorSubject<Entry<any>[]>, subj2: BehaviorSubject<Entry<any>[]>, defaultRes: Entry<any>[]): void {
    const query = subj.value,
          query2 = subj2.value;

    if (query) {
      this.entriesSubject.next(query.filter(el =>
        query2.includes(el)
      ));
    } else {
      this.entriesSubject.next(defaultRes);
    }
  }

  navEntry(entries: Entry<any>[], entry: Entry<any> | null, num: number, gaEvent: boolean = true): void {
    if (entry) {
      const eLength = entries.length,
            currIndex = entries.findIndex(obj => {
              return obj.sys.id === entry.sys.id;
            }),

            newIndex = (): number => {
              if (num > 0)
                return (currIndex + num) % eLength;

              if (num < 0) {
                if (-num >= eLength)
                  return (
                    currIndex - (-num % eLength) + eLength
                  ) % eLength;

                return (
                  currIndex + num + eLength
                ) % eLength;
              }

              return currIndex;
            },

            nextEntryID = entries[newIndex()].sys.id;

      this.getEntry(nextEntryID, null, gaEvent).subscribe();
    }
  }

  async getAssetURL(id: string): Promise<string> {
    const url = [];

    await this.client.getAsset(id).then(
      (res) => url.push(`https:${res.fields.file.url}`)
    )

    return Promise.resolve(url[0]);
  }
}
